/**
 * Reporte: Detalle DDJJ por empresa — Fase 7.3 (reporte 6 de 7)
 *
 * Listado de empleados, sueldos y aportes declarados por una empresa en
 * un período específico. Filtros: empresa + período (required); si hay
 * múltiples DDJJ del mismo período (adicionales), permite elegir cuál.
 */

import { useMemo, useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  Card,
  Button,
  TextField,
  Autocomplete,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';
import DescriptionIcon from '@mui/icons-material/Description';

import { useEmpresas } from '../../hooks/useEmpresas';
import { usePeriodos } from '../../hooks/usePeriodos';
import { usePlanillas, usePlanillaDetalle } from '../../hooks/useDDJJ';
import { formatCurrency, formatDate, formatCuit } from '../../utils/format';
import { getPeriodoLabel, type Periodo } from '../../types/periodo';
import type { PlanillaDetalle } from '../../types/ddjj';

import {
  createReportePdf,
  addReporteTable,
  addReporteFooter,
  fmtCurrency,
  fmtText,
  type ColumnaPdf,
} from '../../utils/reportes/pdf';
import { generarReporteExcel, type ColumnaExcel } from '../../utils/reportes/excel';
import { triggerBlobDownload, buildReporteFilename } from '../../utils/reportes/download';

// ── Componente ────────────────────────────────────────────────────────────────

export default function DetalleDDJJReporte() {
  const [empresaId, setEmpresaId] = useState<string | null>(null);
  const [periodoId, setPeriodoId] = useState<string | null>(null);
  const [planillaId, setPlanillaId] = useState<string | null>(null);

  // ── Datos ──────────────────────────────────────────────────────────────
  const { data: empresasResult } = useEmpresas({ pageSize: 1000 });
  const { data: periodos = [] } = usePeriodos(false); // solo activos
  const empresas = empresasResult?.data ?? [];

  const { data: planillas = [] } = usePlanillas(
    empresaId ?? undefined,
    periodoId ?? undefined,
  );

  // Solo confirmadas — un reporte sobre una DDJJ en borrador no tiene
  // sentido (los sueldos no son definitivos).
  const planillasConfirmadas = useMemo(
    () => planillas.filter((p) => p.estado !== 'borrador'),
    [planillas],
  );

  // Auto-seleccionar la primera planilla confirmada cuando cambian los filtros
  useEffect(() => {
    if (planillasConfirmadas.length > 0) {
      const yaSeleccionada = planillasConfirmadas.find((p) => p.id === planillaId);
      if (!yaSeleccionada) setPlanillaId(planillasConfirmadas[0].id);
    } else {
      setPlanillaId(null);
    }
  }, [planillasConfirmadas, planillaId]);

  const {
    data: detalle = [],
    isLoading,
    isError,
  } = usePlanillaDetalle(planillaId ?? undefined);

  // ── Contexto ───────────────────────────────────────────────────────────
  const empresa = empresas.find((e) => e.id === empresaId);
  const periodo = periodos.find((p) => p.id === periodoId);
  const planillaSeleccionada = planillasConfirmadas.find((p) => p.id === planillaId);

  // ── Rows ───────────────────────────────────────────────────────────────
  const rows = useMemo(
    () =>
      [...detalle].sort((a, b) => a.apellido.localeCompare(b.apellido)),
    [detalle],
  );

  const totales = useMemo(
    () => ({
      empleados: rows.length,
      afiliados: rows.filter((r) => r.es_afiliado).length,
      sumaSueldos: rows.reduce((s, r) => s + Number(r.sueldo), 0),
      sumaAportes: rows.reduce((s, r) => s + Number(r.aporte), 0),
    }),
    [rows],
  );

  const subtitulo = useMemo(() => {
    if (!empresa || !periodo || !planillaSeleccionada) return '';
    const ddjjLabel =
      planillasConfirmadas.length > 1
        ? `DDJJ #${planillaSeleccionada.numero}`
        : 'DDJJ';
    return `${empresa.razon_social} · ${getPeriodoLabel(periodo)} · ${ddjjLabel}`;
  }, [empresa, periodo, planillaSeleccionada, planillasConfirmadas.length]);

  // ── Export PDF ─────────────────────────────────────────────────────────
  const handleExportPdf = () => {
    const doc = createReportePdf({
      titulo: 'Detalle DDJJ por empresa',
      subtitulo,
      orientacion: 'landscape',
    });

    const columnas: ColumnaPdf[] = [
      { header: 'Apellido', format: fmtText },
      { header: 'Nombre', format: fmtText },
      { header: 'CUIL', align: 'center', format: (v) => formatCuit(String(v)) },
      { header: 'Categoría', format: fmtText },
      { header: 'Afiliado', align: 'center', format: (v) => (v ? 'Sí' : 'No') },
      { header: 'Sueldo', align: 'right', format: fmtCurrency },
      { header: '% aporte', align: 'right', format: (v) => (v == null ? '-' : `${Number(v).toFixed(2)}%`) },
      { header: 'Aporte', align: 'right', format: fmtCurrency },
    ];

    const body: unknown[][] = rows.map((r) => [
      r.apellido,
      r.nombre,
      r.cuil,
      r.categoria?.descripcion ?? '—',
      r.es_afiliado,
      r.sueldo,
      r.porcentaje_aporte,
      r.aporte,
    ]);

    body.push([
      `TOTAL (${totales.empleados} empleados, ${totales.afiliados} afiliados)`,
      '',
      '',
      '',
      '',
      totales.sumaSueldos,
      '',
      totales.sumaAportes,
    ]);

    addReporteTable(doc, columnas, body, {
      titulo: 'Detalle DDJJ por empresa',
      subtitulo,
    });
    addReporteFooter(doc);

    const blob = doc.output('blob');
    const nombre = empresa && periodo
      ? `ddjj-${empresa.razon_social}-${periodo.periodo}`
      : 'ddjj-detalle';
    triggerBlobDownload(blob, buildReporteFilename(nombre, 'pdf'));
  };

  // ── Export Excel ───────────────────────────────────────────────────────
  const handleExportExcel = async () => {
    const columnas: ColumnaExcel[] = [
      { header: 'Apellido', key: 'apellido', width: 22, format: 'text' },
      { header: 'Nombre', key: 'nombre', width: 22, format: 'text' },
      { header: 'CUIL', key: 'cuil', width: 14, format: 'text' },
      { header: 'Categoría', key: 'categoria', width: 28, format: 'text' },
      { header: 'Afiliado', key: 'afiliado', width: 10, format: 'text' },
      { header: 'Sueldo', key: 'sueldo', width: 14, format: 'currency' },
      { header: '% aporte', key: 'porcentaje', width: 10, format: 'text' },
      { header: 'Aporte', key: 'aporte', width: 14, format: 'currency' },
    ];

    const rowsExcel = rows.map((r) => ({
      apellido: r.apellido,
      nombre: r.nombre,
      cuil: formatCuit(r.cuil),
      categoria: r.categoria?.descripcion ?? '—',
      afiliado: r.es_afiliado ? 'Sí' : 'No',
      sueldo: r.sueldo,
      porcentaje:
        r.porcentaje_aporte == null ? '-' : `${Number(r.porcentaje_aporte).toFixed(2)}%`,
      aporte: r.aporte,
    }));

    const blob = await generarReporteExcel({
      titulo: 'Detalle DDJJ por empresa',
      subtitulo,
      nombreHoja: 'Detalle DDJJ',
      columnas,
      rows: rowsExcel,
    });
    const nombre = empresa && periodo
      ? `ddjj-${empresa.razon_social}-${periodo.periodo}`
      : 'ddjj-detalle';
    triggerBlobDownload(blob, buildReporteFilename(nombre, 'xlsx'));
  };

  // ── DataGrid ────────────────────────────────────────────────────────────
  const columns: GridColDef<PlanillaDetalle>[] = [
    { field: 'apellido', headerName: 'Apellido', flex: 1, minWidth: 140 },
    { field: 'nombre', headerName: 'Nombre', flex: 1, minWidth: 140 },
    {
      field: 'cuil',
      headerName: 'CUIL',
      width: 140,
      renderCell: (p) => formatCuit(p.row.cuil),
    },
    {
      field: 'categoria',
      headerName: 'Categoría',
      flex: 1.2,
      minWidth: 180,
      renderCell: (p) => p.row.categoria?.descripcion ?? '—',
    },
    {
      field: 'es_afiliado',
      headerName: 'Afiliado',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (p: GridRenderCellParams<PlanillaDetalle>) =>
        p.row.es_afiliado ? (
          <Chip label="Sí" size="small" color="success" />
        ) : (
          <Chip label="No" size="small" />
        ),
    },
    {
      field: 'sueldo',
      headerName: 'Sueldo',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) => formatCurrency(p.row.sueldo),
    },
    {
      field: 'porcentaje_aporte',
      headerName: '% aporte',
      width: 100,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) =>
        p.row.porcentaje_aporte == null
          ? '-'
          : `${Number(p.row.porcentaje_aporte).toFixed(2)}%`,
    },
    {
      field: 'aporte',
      headerName: 'Aporte',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p: GridRenderCellParams<PlanillaDetalle>) => (
        <Typography variant="body2" fontWeight={600}>
          {formatCurrency(p.row.aporte)}
        </Typography>
      ),
    },
  ];

  // ── Estados de UI ───────────────────────────────────────────────────────
  const filtrosCompletos = !!empresaId && !!periodoId;
  const sinPlanillasConfirmadas =
    filtrosCompletos && planillasConfirmadas.length === 0;
  const sinDatos = !!planillaId && !isLoading && !isError && rows.length === 0;

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        component={RouterLink}
        to="/reportes"
        sx={{ mb: 2 }}
      >
        Volver a Reportes
      </Button>

      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <DescriptionIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Detalle DDJJ por empresa
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Empleados, sueldos y aportes de una DDJJ confirmada.
          </Typography>
        </Box>
      </Stack>

      {/* Filtros */}
      <Card sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Filtros
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Autocomplete
            sx={{ minWidth: 280 }}
            size="small"
            options={empresas}
            getOptionLabel={(opt) => opt.razon_social}
            value={empresas.find((e) => e.id === empresaId) ?? null}
            onChange={(_, val) => setEmpresaId(val?.id ?? null)}
            renderInput={(params) => (
              <TextField {...params} label="Empresa (requerida)" required />
            )}
          />

          <Autocomplete
            sx={{ minWidth: 240 }}
            size="small"
            options={periodos}
            getOptionLabel={(p: Periodo) => getPeriodoLabel(p)}
            value={periodos.find((p) => p.id === periodoId) ?? null}
            onChange={(_, val) => setPeriodoId(val?.id ?? null)}
            renderInput={(params) => (
              <TextField {...params} label="Período (requerido)" required />
            )}
          />

          {planillasConfirmadas.length > 1 && (
            <Autocomplete
              sx={{ minWidth: 200 }}
              size="small"
              options={planillasConfirmadas}
              getOptionLabel={(p) => `DDJJ #${p.numero} (${p.estado})`}
              value={planillasConfirmadas.find((p) => p.id === planillaId) ?? null}
              onChange={(_, val) => setPlanillaId(val?.id ?? null)}
              renderInput={(params) => (
                <TextField {...params} label="DDJJ (hay varias)" />
              )}
            />
          )}
        </Stack>
      </Card>

      {/* Totales + Export */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        mb={2}
      >
        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" useFlexGap>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Empleados
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {totales.empleados}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Afiliados
            </Typography>
            <Typography variant="h6" fontWeight={700} color="primary.main">
              {totales.afiliados}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total sueldos
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {formatCurrency(totales.sumaSueldos)}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total aportes
            </Typography>
            <Typography variant="h6" fontWeight={700} color="success.main">
              {formatCurrency(totales.sumaAportes)}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPdf}
            disabled={rows.length === 0}
          >
            Descargar PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<TableViewIcon />}
            onClick={handleExportExcel}
            disabled={rows.length === 0}
          >
            Descargar Excel
          </Button>
        </Stack>
      </Stack>

      {/* Estados */}
      {!filtrosCompletos && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Seleccioná empresa y período para ver el detalle de la DDJJ.
        </Alert>
      )}
      {sinPlanillasConfirmadas && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No hay DDJJ confirmadas para esta empresa y período. Las DDJJ en
          borrador no se incluyen en el reporte.
        </Alert>
      )}
      {planillaSeleccionada && (
        <Alert severity="success" sx={{ mb: 2 }}>
          DDJJ #{planillaSeleccionada.numero} ·{' '}
          confirmada el {formatDate(planillaSeleccionada.fecha_confirmacion)} ·{' '}
          monto total: <strong>{formatCurrency(planillaSeleccionada.monto_total)}</strong>
        </Alert>
      )}
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar el detalle de la DDJJ.
        </Alert>
      )}
      {sinDatos && (
        <Alert severity="info" sx={{ mb: 2 }}>
          La DDJJ no tiene filas de detalle.
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{ border: 0 }}
        />
      </Card>
    </Box>
  );
}
